import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { RequireAuth, RequireNonPartner } from "./components/RequireAuth";
import { RoleHome } from "./components/RoleHome";
import { Login } from "./modules/auth/Login";
import { OrdersInbox } from "./modules/orders/OrdersInbox";

import { Loading } from "./components/Skeleton";

const lz = <T,>(p: Promise<T>, name: keyof T) =>
  lazy(() => p.then((m) => ({ default: m[name] as any })));
const wrap = (el: React.ReactNode) => <Suspense fallback={<Loading />}>{el}</Suspense>;

// Dashboard
const Dashboard = lz(import("./modules/dashboard/DashboardGrid"), "DashboardGrid");

// Prestatie (marge per kanaal/SKU + af te werken kostprijzen)
const Prestatie = lz(import("./modules/prestatie/Prestatie"), "Prestatie");

// Inkoop
const InkoopLayout = lz(import("./modules/inkoop/InkoopLayout"), "InkoopLayout");
const InkoopHome = lz(import("./modules/inkoop/InkoopHome"), "InkoopHome");
const LandedCost = lz(import("./modules/inkoop/LandedCost"), "LandedCost");
const Voorraad = lz(import("./modules/inkoop/Voorraad"), "Voorraad");
const Instellingen = lz(import("./modules/inkoop/Instellingen"), "Instellingen");
const PlanData = lz(import("./modules/inkoop/PlanData"), "PlanData");
const Plan = lz(import("./modules/inkoop/Plan"), "Plan");
const InkoopShipping = lz(import("./modules/inkoop/Shipping"), "Shipping");
const Leveranciers = lz(import("./modules/inkoop/Leveranciers"), "Leveranciers");
const LeverancierDetail = lz(import("./modules/inkoop/LeverancierDetail"), "LeverancierDetail");
const Bestellingen = lz(import("./modules/inkoop/Bestellingen"), "Bestellingen");
const BestellingDetail = lz(import("./modules/inkoop/BestellingDetail"), "BestellingDetail");
const B2BPrijzen = lz(import("./modules/inkoop/B2BPrijzen"), "B2BPrijzen");
const RapportageBouwer = lz(import("./modules/rapportages/Bouwer"), "Bouwer");

// Finance
const FinanceLayout = lz(import("./modules/finance/FinanceLayout"), "FinanceLayout");
const FinanceCashflow = lz(import("./modules/finance/Cashflow"), "Cashflow");
const Costs = lz(import("./modules/costs/Costs"), "Costs");
const Marketing = lz(import("./modules/marketing/Marketing"), "Marketing");
const BolAds = lz(import("./modules/marketing/BolAds"), "BolAds");
const Btw = lz(import("./modules/btw/Btw"), "Btw");
const BtwAangifte = lz(import("./modules/finance/btw/BtwAangifte"), "BtwAangifte");
const FacturenLijst = lz(import("./modules/finance/facturen/FacturenLijst"), "FacturenLijst");
const FactuurDetail = lz(import("./modules/finance/facturen/FactuurDetail"), "FactuurDetail");

// Orders / Returns / Overrides
const OrdersLayout = lz(import("./modules/orders/OrdersLayout"), "OrdersLayout");
// OrdersInbox eager imported (top of file) zodat de mode-prop typed werkt
const OrderDetail = lz(import("./modules/orders/OrderDetail"), "OrderDetail");
const Returns = lz(import("./modules/returns/Returns"), "Returns");
const ReturnsScan = lz(import("./modules/returns/ReturnsScan"), "ReturnsScan");
const Overrides = lz(import("./modules/overrides/Overrides"), "Overrides");
const OrdersB2B = lz(import("./modules/orders/OrdersB2B"), "OrdersB2B");

// Warehouse
const WarehouseLayout = lz(import("./modules/warehouse/WarehouseLayout"), "WarehouseLayout");
const NodStockHome = lz(import("./modules/wms/NodStockHome"), "NodStockHome");
const NodStockSerialConfig = lz(import("./modules/wms/NodStockSerialConfig"), "NodStockSerialConfig");
const NodStockAssortiment = lz(import("./modules/wms/NodStockAssortiment"), "NodStockAssortiment");
const NodStockLayout = lz(import("./modules/wms/NodStockLayout"), "NodStockLayout");
// v2 (post-cleanup 2026-08-18) — enige bron: nod_movements
const NodStockVoorraadV2 = lz(import("./modules/wms/NodStockVoorraadV2").then(m => ({ default: m.NodStockVoorraadV2 })), "default");
const NodStockBewegingenV2 = lz(import("./modules/wms/NodStockBewegingenV2").then(m => ({ default: m.NodStockBewegingenV2 })), "default");
const NodStockInboundV2 = lz(import("./modules/wms/NodStockInboundV2").then(m => ({ default: m.NodStockInboundV2 })), "default");
const NodStockOutboundV2 = lz(import("./modules/wms/NodStockOutboundV2").then(m => ({ default: m.NodStockOutboundV2 })), "default");
const NodStockPaklijstenV2 = lz(import("./modules/wms/NodStockPaklijstenV2").then(m => ({ default: m.NodStockPaklijstenV2 })), "default");
const NodStockTellingen = lz(import("./modules/wms/NodStockTellingen").then(m => ({ default: m.NodStockTellingen })), "default");
const NodStockInslagTaken = lz(import("./modules/wms/NodStockInslagTaken"), "NodStockInslagTaken");
const NodPortalShell = lz(import("./modules/wms/NodPortalShell"), "NodPortalShell");
const NodPortalVoorraad = lz(import("./modules/wms/NodPortalVoorraad"), "NodPortalVoorraad");
const NodPortalBewegingen = lz(import("./modules/wms/NodPortalBewegingen"), "NodPortalBewegingen");
const NodPortalLeveringen = lz(import("./modules/wms/NodPortalLeveringen"), "NodPortalLeveringen");
const NodPortalWachtwoord = lz(import("./modules/wms/NodPortalWachtwoord"), "NodPortalWachtwoord");
// WMS v2 (TC27 mobile scan) — losstaande routes buiten AppShell (fullscreen).
const WmsMobileHome = lz(import("./modules/wms-mobile/WmsMobileHome").then(m => ({ default: m.WmsMobileHome })), "default");
const WmsPutawayFlow = lz(import("./modules/wms-mobile/WmsPutawayFlow").then(m => ({ default: m.WmsPutawayFlow })), "default");
const WmsMoveFlow = lz(import("./modules/wms-mobile/WmsMoveFlow").then(m => ({ default: m.WmsMoveFlow })), "default");
const WmsPickFlow = lz(import("./modules/wms-mobile/WmsPickFlow").then(m => ({ default: m.WmsPickFlow })), "default");
const WmsCountingFlow = lz(import("./modules/wms-mobile/WmsCountingFlow").then(m => ({ default: m.WmsCountingFlow })), "default");
const WmsNulmetingFlow = lz(import("./modules/wms-mobile/WmsNulmetingFlow"), "WmsNulmetingFlow");
const WmsIndelingFlow = lz(import("./modules/wms-mobile/WmsIndelingFlow"), "WmsIndelingFlow");
const WmsTakenFlow = lz(import("./modules/wms-mobile/WmsTakenFlow"), "WmsTakenFlow");
const WmsInslagFlow = lz(import("./modules/wms-mobile/WmsInslagFlow"), "WmsInslagFlow");
const WmsVoorraadFlow = lz(import("./modules/wms-mobile/WmsVoorraadFlow"), "WmsVoorraadFlow");
const WmsViewport = lz(import("./modules/wms-mobile/WmsViewport"), "WmsViewport");
const WmsScannerHarness = lz(import("./modules/wms/WmsScannerHarness"), "WmsScannerHarness");
const WmsIndelingOverzicht = lz(import("./modules/wms/WmsIndelingOverzicht"), "WmsIndelingOverzicht");
const WmsLabelFlow = lz(import("./modules/wms-mobile/WmsLabelFlow").then(m => ({ default: m.WmsLabelFlow })), "default");
const WmsComponentsDemo = lz(import("./modules/wms-mobile/WmsComponentsDemo").then(m => ({ default: m.WmsComponentsDemo })), "default");
const WmsPutawayFlowV2 = lz(import("./modules/wms-mobile/WmsPutawayFlowV2").then(m => ({ default: m.WmsPutawayFlowV2 })), "default");
const WmsScanLab = lz(import("./modules/wms-mobile/WmsScanLab").then(m => ({ default: m.WmsScanLab })), "default");
const WmsManualOutboundFlow = lz(import("./modules/wms-mobile/WmsManualOutboundFlow").then(m => ({ default: m.WmsManualOutboundFlow })), "default");
const VoorraadPage = lz(import("./modules/warehouse/Voorraad"), "VoorraadPage");
const InkomendPage = lz(import("./modules/warehouse/Inkomend"), "InkomendPage");
const MutatieLog = lz(import("./modules/warehouse/MutatieLog"), "MutatieLog");

// Producten / Klanten
const ProductenAssortment = lz(import("./modules/producten/ProductenAssortment"), "ProductenAssortment");
const ProductenSiuRaw = lz(import("./modules/producten/ProductenSiuRaw"), "ProductenSiuRaw");
const ProductDetail = lz(import("./modules/producten/ProductDetail"), "ProductDetail");
const KlantenLijst = lz(import("./modules/klanten/KlantenLijst"), "KlantenLijst");
const KlantDetail = lz(import("./modules/klanten/KlantDetail"), "KlantDetail");

// CS Inbox — Microsoft Graph koppeling voor service@ (fase 1 read-only)
const CsInboxLijst = lz(import("./modules/cs-inbox/CsInboxLijst"), "CsInboxLijst");
const CsInboxDetail = lz(import("./modules/cs-inbox/CsInboxDetail"), "CsInboxDetail");

// Rapportages / Reconciliation
const Rapportages = lz(import("./modules/rapportages/Rapportages"), "Rapportages");
const Reconciliation = lz(import("./modules/reconciliation/Reconciliation"), "Reconciliation");

// Taken
const TakenLijst = lz(import("./modules/taken/TakenLijst"), "TakenLijst");
const TaakNieuw = lz(import("./modules/taken/TaakNieuw"), "TaakNieuw");
const TaakDetail = lz(import("./modules/taken/TaakDetail"), "TaakDetail");

// Beheer
const BeheerLayout = lz(import("./modules/beheer/BeheerLayout"), "BeheerLayout");
const Gebruikers = lz(import("./modules/beheer/Gebruikers"), "Gebruikers");
const GebruikerNieuw = lz(import("./modules/beheer/GebruikerNieuw"), "GebruikerNieuw");
const GebruikerDetail = lz(import("./modules/beheer/GebruikerDetail"), "GebruikerDetail");
const GebruikerOverzicht = lz(import("./modules/beheer/GebruikerDetail"), "GebruikerOverzicht");
const GebruikerRechten = lz(import("./modules/beheer/GebruikerDetail"), "GebruikerRechten");
const GebruikerActiviteit = lz(import("./modules/beheer/GebruikerDetail"), "GebruikerActiviteit");
const AuditLog = lz(import("./modules/beheer/AuditLog"), "AuditLog");
const BeheerInstellingen = lz(import("./modules/beheer/Instellingen"), "Instellingen");
const Integraties = lz(import("./modules/beheer/Integraties"), "Integraties");
const NodPartners = lz(import("./modules/beheer/NodPartners"), "NodPartners");
// Nieuwe gemerge Beheer → Marketplaces hub + tabbed pagina's per marketplace
const BeheerMarketplacesHub = lz(import("./modules/beheer/BeheerMarketplacesHub"), "BeheerMarketplacesHub");
const LeadtimeHub = lz(import("./modules/beheer/LeadtimeHub").then(m => ({ default: m.LeadtimeHub })), "default");
const AmazonLeverwijze = lz(import("./modules/beheer/AmazonLeverwijze").then(m => ({ default: m.AmazonLeverwijze })), "default");
const BeheerMarketplaceAmazon = lz(import("./modules/beheer/BeheerMarketplaceAmazon"), "BeheerMarketplaceAmazon");
const BeheerMarketplaceBol = lz(import("./modules/beheer/BeheerMarketplaceBol"), "BeheerMarketplaceBol");
const BeheerMarketplaceKaufland = lz(import("./modules/beheer/BeheerMarketplaceKaufland"), "BeheerMarketplaceKaufland");
const BeheerMarketplaceMediamarkt = lz(import("./modules/beheer/BeheerMarketplaceMediamarkt"), "BeheerMarketplaceMediamarkt");
const BeheerMarketplaceShopify = lz(import("./modules/beheer/BeheerMarketplaceShopify"), "BeheerMarketplaceShopify");
const MarketplacesHealth = lz(import("./modules/beheer/MarketplacesHealth"), "MarketplacesHealth");
const AmazonRolloutBatches = lz(import("./modules/amazon/AmazonRolloutBatches"), "AmazonRolloutBatches");
const AmazonRolloutBatchNew = lz(import("./modules/amazon/AmazonRolloutBatches"), "AmazonRolloutBatchNew");
const AmazonRolloutBatchDetail = lz(import("./modules/amazon/AmazonRolloutBatches"), "AmazonRolloutBatchDetail");

// Prijzen — tool-native engine UI + legacy CSV push-batches
const PrijzenProducten = lz(import("./modules/prijzen/PrijzenProducten"), "PrijzenProducten");
const PrijzenSkuDetail = lz(import("./modules/prijzen/PrijzenSkuDetail"), "PrijzenSkuDetail");
const PrijzenInstellingen = lz(import("./modules/prijzen/PrijzenInstellingen"), "PrijzenInstellingen");
const PrijzenPush = lz(import("./modules/prijzen/PrijzenPush"), "PrijzenPush");
const PrijzenHistorie = lz(import("./modules/prijzen/PrijzenHistorie"), "PrijzenHistorie");
// Legacy CSV push-batches (blijven beschikbaar)
const PrijzenLijst = lz(import("./modules/prijzen/PrijzenLijst"), "PrijzenLijst");
const PrijzenNieuw = lz(import("./modules/prijzen/PrijzenNieuw"), "PrijzenNieuw");
const PrijzenBatch = lz(import("./modules/prijzen/PrijzenBatch"), "PrijzenBatch");

// Verkopen-module (operations: orders / producten / communicatie / sync)
const VerkopenLayout = lz(import("./modules/verkopen/VerkopenLayout"), "VerkopenLayout");
const VerkopenProducten = lz(import("./modules/verkopen/VerkopenProducten"), "VerkopenProducten");

// Amazon operations (orders + communicatie + sync — onder /verkopen)
const AmazonOrders = lz(import("./modules/amazon/AmazonOrders"), "AmazonOrders");
const AmazonOrderDetail = lz(import("./modules/amazon/AmazonOrderDetail"), "AmazonOrderDetail");
const AmazonCommunicatie = lz(import("./modules/amazon/AmazonCommunicatie"), "AmazonCommunicatie");
const AmazonSync = lz(import("./modules/amazon/AmazonSync"), "AmazonSync");

// Misc
const Progress = lz(import("./modules/progress/Progress"), "Progress");
const Users = lz(import("./modules/admin/Users"), "Users");

export const router = createBrowserRouter([
  { path: "/login", element: <Login /> },
  // WMS v2 mobile (TC27) — fullscreen, buiten AppShell, alleen Merkatura-users.
  // partner_nod krijgt 403 op de v2-endpoints (permission wms.access ontbreekt).
  {
    path: "/wms/mobile",
    element: (
      <RequireAuth>
        <RequireNonPartner>
          <Outlet />
        </RequireNonPartner>
      </RequireAuth>
    ),
    children: [
      { index: true,     element: <Navigate to="/wms/mobile/home" replace /> },
      { path: "home",    element: wrap(<WmsMobileHome />) },
      { path: "putaway", element: wrap(<WmsPutawayFlow />) },
      { path: "move",    element: wrap(<WmsMoveFlow />) },
      { path: "pick",     element: wrap(<WmsPickFlow />) },
      { path: "counting", element: wrap(<WmsCountingFlow />) },
      { path: "nulmeting", element: wrap(<WmsNulmetingFlow />) },
      // Opvolger van de nulmeting: legt de PLEK vast, niet de voorraad.
      { path: "indeling", element: wrap(<WmsIndelingFlow />) },
      // Gedeelde verplaatstakenlijst: taken uit geaccepteerde adviezen (wms3d).
      { path: "taken",    element: wrap(<WmsTakenFlow />) },
      // Inslag: pakbonregels wegzetten op het goedgekeurde vak (0231).
      { path: "inslag",   element: wrap(<WmsInslagFlow />) },
      // Alleen lezen: zoeken, saldo, plek en de bewegingen per product.
      { path: "voorraad", element: wrap(<WmsVoorraadFlow />) },
      { path: "label",    element: wrap(<WmsLabelFlow />) },
      { path: "_components", element: wrap(<WmsComponentsDemo />) },
      { path: "putaway-v2", element: wrap(<WmsPutawayFlowV2 />) },
      { path: "scan-lab", element: wrap(<WmsScanLab />) },
      { path: "manual-outbound", element: wrap(<WmsManualOutboundFlow />) },
      // Leest de echte CSS-maten van het apparaat; voedt de testharnas.
      { path: "viewport", element: wrap(<WmsViewport />) },
    ],
  },
  // NOD-partner portal: eigen shell, geen Merkatura-modules toegankelijk
  {
    path: "/nod-portal",
    element: (
      <RequireAuth>
        {wrap(<NodPortalShell />)}
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/nod-portal/stock" replace /> },
      { path: "stock",      element: wrap(<NodPortalVoorraad />) },
      { path: "movements",  element: wrap(<NodPortalBewegingen />) },
      { path: "deliveries", element: wrap(<NodPortalLeveringen />) },
      { path: "password",   element: wrap(<NodPortalWachtwoord />) },
      // Legacy NL-URL's: redirect zodat bookmarks blijven werken
      { path: "voorraad",   element: <Navigate to="/nod-portal/stock" replace /> },
      { path: "bewegingen", element: <Navigate to="/nod-portal/movements" replace /> },
      { path: "leveringen", element: <Navigate to="/nod-portal/deliveries" replace /> },
      { path: "wachtwoord", element: <Navigate to="/nod-portal/password" replace /> },
    ],
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        <RequireNonPartner>
          <AppShell />
        </RequireNonPartner>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <RoleHome /> },
      { path: "dashboard", element: wrap(<Dashboard />) },
      { path: "prestatie", element: wrap(<Prestatie />) },

      // === Module 2: Orders ===
      // Order-detail blijft op /orders/:id (top-level) zodat deep-links uit
      // andere modules werken. Sub-tabs onder /orders via OrdersLayout.
      // 5 status-buckets + Overrides + Alle = 7 tabs (geïnspireerd op
      // Sendcloud/Stockitup referenties in /voorbeelden/orders).
      {
        path: "orders",
        element: wrap(<OrdersLayout />),
        children: [
          { index: true, element: wrap(<OrdersInbox mode="te-verzenden" />) },
          { path: "verzonden", element: wrap(<OrdersInbox mode="verzonden" />) },
          { path: "geleverd", element: wrap(<OrdersInbox mode="geleverd" />) },
          { path: "returns", element: wrap(<Returns />) },
          { path: "returns/scan", element: wrap(<ReturnsScan />) },
          { path: "cancelled", element: wrap(<OrdersInbox mode="cancelled" />) },
          { path: "overrides", element: wrap(<Overrides />) },
          { path: "b2b", element: wrap(<OrdersB2B />) },
          { path: "alle", element: wrap(<OrdersInbox mode="alle" />) },
        ],
      },
      { path: "orders/:id", element: wrap(<OrderDetail />) },

      // === Module: CS Inbox === (Microsoft Graph koppeling voor service@)
      { path: "cs-inbox", element: wrap(<CsInboxLijst />) },
      { path: "cs-inbox/:id", element: wrap(<CsInboxDetail />) },

      // === Module 3: Warehouse ===
      {
        path: "warehouse",
        element: wrap(<WarehouseLayout />),
        children: [
          { index: true, element: <Navigate to="/warehouse/voorraad" replace /> },
          { path: "voorraad", element: wrap(<VoorraadPage />) },
          { path: "inkomend", element: wrap(<InkomendPage />) },
          { path: "mutaties", element: wrap(<MutatieLog />) },
        ],
      },

      // === Module 3b: NOD Stock (WMS) ===
      // Code/perms/tabellen blijven wms.*/nod_*; UI-labels zijn "NOD Stock".
      // Wrapper-layout met i18n-provider + taal-switcher.
      // Post-cleanup 2026-08-18: nod_movements = enige bron van voorraad.
      // Deprecated (leiden nu allemaal terug naar de v2-pagina's):
      //   inslag/uitslag/expected-deliveries/cos-picklijst/locaties/tellingen
      {
        path: "nod-stock",
        element: wrap(<NodStockLayout />),
        children: [
          { index: true,                            element: wrap(<NodStockHome />) },
          { path: "voorraad",                       element: wrap(<NodStockVoorraadV2 />) },
          { path: "scanner-harnas",                 element: wrap(<WmsScannerHarness />) },
          { path: "locatie-indeling",               element: wrap(<WmsIndelingOverzicht />) },
          { path: "bewegingen",                     element: wrap(<NodStockBewegingenV2 />) },
          { path: "inbound",                        element: wrap(<NodStockInboundV2 />) },
          { path: "inslag-taken",                   element: wrap(<NodStockInslagTaken />) },
          { path: "outbound",                       element: wrap(<NodStockOutboundV2 />) },
          { path: "paklijsten",                     element: wrap(<NodStockPaklijstenV2 />) },
          { path: "tellingen",                      element: wrap(<NodStockTellingen />) },
          { path: "serial-config",                  element: wrap(<NodStockSerialConfig />) },
          { path: "assortiment",                    element: wrap(<NodStockAssortiment />) },
          // Legacy redirects
          { path: "inslag",                         element: <Navigate to="/nod-stock/inbound" replace /> },
          { path: "uitslag",                        element: <Navigate to="/nod-stock/outbound" replace /> },
          { path: "expected-deliveries",            element: <Navigate to="/nod-stock/inbound" replace /> },
          { path: "expected-deliveries/new",        element: <Navigate to="/nod-stock/inbound" replace /> },
          { path: "cos-picklijst",                  element: <Navigate to="/nod-stock/outbound" replace /> },
          { path: "locaties",                       element: <Navigate to="/nod-stock/voorraad" replace /> },
          { path: "tellingen",                      element: <Navigate to="/nod-stock/voorraad" replace /> },
        ],
      },

      // === Module 4: Producten ===
      // Default = Fase 3 curated assortment master. Oude SIU-only catalogus
      // blijft beschikbaar als debug-view onder /producten/siu-raw.
      { path: "producten", element: wrap(<ProductenAssortment />) },
      { path: "producten/siu-raw", element: wrap(<ProductenSiuRaw />) },
      { path: "producten/:sku", element: wrap(<ProductDetail />) },

      // === Module 5: Inkoop (bestaand) ===
      {
        path: "inkoop",
        element: wrap(<InkoopLayout />),
        children: [
          { index: true, element: wrap(<InkoopHome />) },
          { path: "landed-cost", element: wrap(<LandedCost />) },
          { path: "voorraad", element: wrap(<Voorraad />) },
          { path: "plan-data", element: wrap(<PlanData />) },
          { path: "plan", element: wrap(<Plan />) },
          { path: "leveranciers", element: wrap(<Leveranciers />) },
          { path: "leveranciers/:id", element: wrap(<LeverancierDetail />) },
          { path: "bestellingen", element: wrap(<Bestellingen />) },
          { path: "bestellingen/:id", element: wrap(<BestellingDetail />) },
          { path: "b2b-prijzen", element: wrap(<B2BPrijzen />) },
          { path: "shipping", element: wrap(<InkoopShipping />) },
          { path: "cashflow", element: <Navigate to="/finance" replace /> },
          { path: "instellingen", element: wrap(<Instellingen />) },
        ],
      },

      // Oude /marketplaces performance-page verwijderd — vervangen door Verkopen + Beheer/Marketplaces.
      { path: "marketplaces", element: <Navigate to="/beheer/marketplaces" replace /> },
      { path: "marketplaces/:name", element: <Navigate to="/beheer/marketplaces" replace /> },

      // === Module Prijzen — top-level (tool-native engine) ===
      { path: "prijzen", element: wrap(<PrijzenProducten />) },
      { path: "prijzen/sku/:sku", element: wrap(<PrijzenSkuDetail />) },
      { path: "prijzen/instellingen", element: wrap(<PrijzenInstellingen />) },
      { path: "prijzen/push", element: wrap(<PrijzenPush />) },
      { path: "prijzen/historie", element: wrap(<PrijzenHistorie />) },
      { path: "prijzen/batches", element: wrap(<PrijzenLijst />) },
      { path: "prijzen/batches/nieuw", element: wrap(<PrijzenNieuw />) },
      { path: "prijzen/nieuw", element: wrap(<PrijzenNieuw />) },
      { path: "prijzen/batches/:id", element: wrap(<PrijzenBatch />) },
      { path: "prijzen/:id", element: wrap(<PrijzenBatch />) },

      // === Verkopen — operationele kant van marketplaces ===
      // Vervangt de losse /amazon, /bol, /kaufland, /mediamarkt, /shopify
      // top-level paden. Per-marketplace config staat onder /beheer/marketplaces/{naam}.
      {
        path: "verkopen",
        element: wrap(<VerkopenLayout />),
        children: [
          { index: true, element: <Navigate to="/verkopen/orders" replace /> },
          { path: "orders", element: wrap(<AmazonOrders />) },
          { path: "orders/:order_id", element: wrap(<AmazonOrderDetail />) },
          { path: "producten", element: wrap(<VerkopenProducten />) },
          { path: "communicatie", element: wrap(<AmazonCommunicatie />) },
          { path: "sync", element: wrap(<AmazonSync />) },
        ],
      },

      // === Backwards-compat redirects voor oude marketplace-paden ===
      { path: "amazon", element: <Navigate to="/verkopen/orders" replace /> },
      { path: "amazon/orders", element: <Navigate to="/verkopen/orders" replace /> },
      { path: "amazon/orders/:order_id", element: <Navigate to="/verkopen/orders" replace /> },
      { path: "amazon/producten", element: <Navigate to="/verkopen/producten?marketplace=amazon" replace /> },
      { path: "amazon/communicatie", element: <Navigate to="/verkopen/communicatie" replace /> },
      { path: "amazon/sync", element: <Navigate to="/verkopen/sync" replace /> },
      { path: "amazon/magazijnen", element: <Navigate to="/beheer/marketplaces/amazon?tab=magazijnen" replace /> },
      { path: "amazon/easyship", element: <Navigate to="/beheer/marketplaces/amazon?tab=routing" replace /> },
      { path: "mediamarkt", element: <Navigate to="/verkopen/producten?marketplace=mediamarkt" replace /> },
      { path: "bol", element: <Navigate to="/verkopen/producten?marketplace=bol" replace /> },
      { path: "kaufland", element: <Navigate to="/verkopen/producten?marketplace=kaufland" replace /> },
      { path: "shopify", element: <Navigate to="/verkopen/producten?marketplace=shopify" replace /> },
      { path: "levertijden-switch", element: <Navigate to="/beheer/marketplaces" replace /> },
      { path: "levertijden-switch/:marketplace", element: <Navigate to="/beheer/marketplaces" replace /> },
      { path: "levertijden", element: wrap(<LeadtimeHub />) },
      { path: "amazon-leverwijze", element: wrap(<AmazonLeverwijze />) },
      { path: "levertijden-per-sku", element: <Navigate to="/beheer/levertijden" replace /> },

      // === Module 7: Klanten ===
      { path: "klanten", element: wrap(<KlantenLijst />) },
      { path: "klanten/:hash", element: wrap(<KlantDetail />) },

      // === Module 8: Finance (bestaand) ===
      {
        path: "finance",
        element: wrap(<FinanceLayout />),
        children: [
          { index: true, element: wrap(<FinanceCashflow />) },
          { path: "overheadkosten", element: wrap(<Costs />) },
          { path: "marketingkosten", element: wrap(<Marketing />) },
          { path: "btw", element: wrap(<Btw />) },
          { path: "btw-aangifte", element: wrap(<BtwAangifte />) },
          { path: "facturen", element: wrap(<FacturenLijst />) },
          { path: "facturen/:id", element: wrap(<FactuurDetail />) },
        ],
      },

      // === Module 9: Marketing & Content ===
      // De placeholder is vervangen door de Bol Advertising-module (fase 1:
      // monitoren). SEO Toolkit en content review staan nog open en krijgen
      // te zijner tijd hun eigen route onder dit pad.
      { path: "marketing-content", element: wrap(<BolAds />) },

      // === Module 10: Rapportages ===
      { path: "rapportages", element: wrap(<Rapportages />) },
      { path: "rapportages/bouwer", element: wrap(<RapportageBouwer />) },

      // === Module 11: Taken ===
      { path: "taken", element: wrap(<TakenLijst />) },
      { path: "taken/nieuw", element: wrap(<TaakNieuw />) },
      { path: "taken/:id", element: wrap(<TaakDetail />) },

      // === Module 12: Reconciliation ===
      { path: "reconciliation", element: wrap(<Reconciliation />) },

      // === Module 13: Beheer ===
      {
        path: "beheer",
        element: wrap(<BeheerLayout />),
        children: [
          { index: true, element: <Navigate to="/beheer/gebruikers" replace /> },
          { path: "gebruikers", element: wrap(<Gebruikers />) },
          { path: "gebruikers/nieuw", element: wrap(<GebruikerNieuw />) },
          {
            path: "gebruikers/:id",
            element: wrap(<GebruikerDetail />),
            children: [
              { index: true, element: wrap(<GebruikerOverzicht />) },
              { path: "rechten", element: wrap(<GebruikerRechten />) },
              { path: "activiteit", element: wrap(<GebruikerActiviteit />) },
            ],
          },
          { path: "audit-log", element: wrap(<AuditLog />) },
          { path: "integraties", element: wrap(<Integraties />) },
          { path: "nod-partners", element: wrap(<NodPartners />) },
          // === Marketplaces — geconsolideerd (vervangt losse {marketplace}-api + voorraad-sync routes) ===
          { path: "marketplaces", element: wrap(<BeheerMarketplacesHub />) },
          { path: "marketplaces/health", element: wrap(<MarketplacesHealth />) },
          { path: "marketplaces/amazon", element: wrap(<BeheerMarketplaceAmazon />) },
          { path: "marketplaces/amazon/rollouts", element: wrap(<AmazonRolloutBatches />) },
          { path: "marketplaces/amazon/rollouts/new", element: wrap(<AmazonRolloutBatchNew />) },
          { path: "marketplaces/amazon/rollouts/:id", element: wrap(<AmazonRolloutBatchDetail />) },
          { path: "marketplaces/bol", element: wrap(<BeheerMarketplaceBol />) },
          { path: "marketplaces/kaufland", element: wrap(<BeheerMarketplaceKaufland />) },
          { path: "marketplaces/mediamarkt", element: wrap(<BeheerMarketplaceMediamarkt />) },
          { path: "marketplaces/shopify", element: wrap(<BeheerMarketplaceShopify />) },
          // === Prijzen — tool-native engine UI ===
          // Default page = producten-overzicht met advies per kanaal.
          // Sub-routes: sku-detail, instellingen, push-wizard, historie.
          // Prijzen-module verhuisd naar top-level /prijzen — backwards-compat redirects naar root.
          // Parametrized subroutes redirecten naar de lijst (niemand heeft deep-links nog).
          { path: "prijzen", element: <Navigate to="/prijzen" replace /> },
          { path: "prijzen/instellingen", element: <Navigate to="/prijzen/instellingen" replace /> },
          { path: "prijzen/push", element: <Navigate to="/prijzen/push" replace /> },
          { path: "prijzen/historie", element: <Navigate to="/prijzen/historie" replace /> },
          { path: "prijzen/batches", element: <Navigate to="/prijzen/batches" replace /> },
          { path: "prijzen/nieuw", element: <Navigate to="/prijzen/nieuw" replace /> },
          { path: "prijzen/*", element: <Navigate to="/prijzen" replace /> },
          // Backwards-compat redirects naar nieuwe locaties
          { path: "amazon-sp-api", element: <Navigate to="/beheer/marketplaces/amazon?tab=api" replace /> },
          { path: "mediamarkt-api", element: <Navigate to="/beheer/marketplaces/mediamarkt?tab=api" replace /> },
          { path: "bol-api", element: <Navigate to="/beheer/marketplaces/bol?tab=api" replace /> },
          { path: "kaufland-api", element: <Navigate to="/beheer/marketplaces/kaufland?tab=api" replace /> },
          { path: "shopify-api", element: <Navigate to="/beheer/marketplaces/shopify?tab=api" replace /> },
          { path: "voorraad-sync", element: <Navigate to="/beheer/marketplaces" replace /> },
          { path: "voorraad-sync/amazon", element: <Navigate to="/beheer/marketplaces/amazon?tab=routing" replace /> },
          { path: "voorraad-sync/bol", element: <Navigate to="/beheer/marketplaces/bol?tab=voorraad-sync" replace /> },
          { path: "voorraad-sync/kaufland", element: <Navigate to="/beheer/marketplaces/kaufland?tab=voorraad-sync" replace /> },
          { path: "voorraad-sync/mediamarkt", element: <Navigate to="/beheer/marketplaces/mediamarkt?tab=voorraad-sync" replace /> },
          { path: "voorraad-sync/shopify", element: <Navigate to="/beheer/marketplaces/shopify?tab=voorraad-sync" replace /> },
          { path: "instellingen", element: wrap(<BeheerInstellingen />) },
          { path: "project-voortgang", element: wrap(<Progress />) },
          // === Levertijden & Amazon leverwijze ===
          { path: "levertijden", element: wrap(<LeadtimeHub />) },
          { path: "amazon-leverwijze", element: wrap(<AmazonLeverwijze />) },
        ],
      },

      // === Project voortgang (geen permission, voor iedereen) ===
      { path: "progress", element: wrap(<Progress />) },

      // === Backward-compat URLs (blijven werken; staan niet meer in sidebar) ===
      { path: "overrides", element: <Navigate to="/orders/overrides" replace /> },
      { path: "returns", element: <Navigate to="/orders/returns" replace /> },
      { path: "returns/scan", element: <Navigate to="/orders/returns/scan" replace /> },
      { path: "admin/users", element: <Navigate to="/beheer/gebruikers" replace /> },
      { path: "admin/users-old", element: wrap(<Users />) },
      { path: "costs", element: <Navigate to="/finance/overheadkosten" replace /> },
      { path: "marketing", element: <Navigate to="/finance/marketingkosten" replace /> },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);
